import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dashboardAPI } from '../services/api';

const formatCurrencyRounded = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
}).format(Math.round(Number(amount || 0)));

const buildCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const parseMonthFromQuery = (search) => {
  const monthFromQuery = new URLSearchParams(search).get('month');
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthFromQuery || '')) {
    return monthFromQuery;
  }
  return buildCurrentMonthValue();
};

const formatMonthLabel = (monthValue) => {
  const [year, month] = String(monthValue || '').split('-').map(Number);
  if (!year || !month) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, 1));
};

const getChangeClass = (amount) => {
  const rounded = Math.round(Number(amount || 0));
  if (rounded > 0) {
    return 'text-rose-600';
  }
  if (rounded < 0) {
    return 'text-emerald-600';
  }
  return 'text-slate-500';
};

const getChangeArrow = (amount) => {
  const rounded = Math.round(Number(amount || 0));
  if (rounded > 0) {
    return '\u2191';
  }
  if (rounded < 0) {
    return '\u2193';
  }
  return '\u2192';
};

function CategoryComparison() {
  const location = useLocation();
  const sectionRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const [selectedMonth, setSelectedMonth] = useState(() => parseMonthFromQuery(location.search));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);

  useEffect(() => {
    setSelectedMonth(parseMonthFromQuery(location.search));
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      const requestId = fetchRequestIdRef.current + 1;
      fetchRequestIdRef.current = requestId;

      const applyData = (nextData) => {
        if (fetchRequestIdRef.current !== requestId) {
          return;
        }
        setDashboardData(nextData);
      };

      try {
        setLoading(true);
        setError('');
        const [year, month] = selectedMonth.split('-').map(Number);
        const data = await dashboardAPI.get(month, year, {
          staleWhileRevalidate: true,
          onBackgroundUpdate: applyData,
        });
        applyData(data);
      } catch (fetchError) {
        if (fetchRequestIdRef.current !== requestId) {
          return;
        }
        console.error('Error loading category comparison data:', fetchError);
        setError('Failed to load category comparison');
        setDashboardData(null);
      } finally {
        if (fetchRequestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [selectedMonth]);

  useEffect(() => {
    setExpandedCategoryId(null);
  }, [selectedMonth]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const sectionNode = sectionRef.current;
      if (!sectionNode) {
        return;
      }
      if (!sectionNode.contains(event.target)) {
        setExpandedCategoryId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const comparisonRows = useMemo(() => {
    const rows = dashboardData?.insights?.details?.monthOverMonthComparison;
    return Array.isArray(rows) ? rows : [];
  }, [dashboardData]);

  const totals = useMemo(() => {
    return comparisonRows.reduce((accumulator, entry) => ({
      currentValue: accumulator.currentValue + Number(entry.currentValue || 0),
      previousValue: accumulator.previousValue + Number(entry.previousValue || 0),
      difference: accumulator.difference + Number(entry.difference || 0),
    }), { currentValue: 0, previousValue: 0, difference: 0 });
  }, [comparisonRows]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-slate-500">Loading comparison...</div>;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="text-center">
          <p className="text-slate-700">{error}</p>
          <Link to={`/dashboard?month=${selectedMonth}`} className="mt-3 inline-block text-sm font-medium text-blue-700 hover:text-blue-800">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link
          to={`/dashboard?month=${selectedMonth}`}
          aria-label="Back to Dashboard"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-slate-50 text-base font-semibold text-blue-700 hover:bg-blue-100"
        >
          {'\u2039'}
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 leading-tight">Category Comparison</h1>
          <p className="text-sm text-slate-600 mt-1 leading-tight">{formatMonthLabel(selectedMonth)} vs previous month</p>
        </div>
      </div>

      <section
        ref={sectionRef}
        onClick={() => setExpandedCategoryId(null)}
        className="rounded-3xl border border-slate-200/80 bg-white/90 backdrop-blur p-5 md:p-6 shadow-sm"
      >
        {comparisonRows.length > 0 ? (
          <>
            <div className="sm:hidden">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[40%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-300 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <th className="sticky top-0 z-10 bg-slate-100 py-2 text-left">Category</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-2 pl-2 text-right">Cur</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-2 pl-2 text-right">Prev</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-2 pl-2 text-right">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((entry) => {
                    const isExpanded = expandedCategoryId === entry.id;
                    const children = Array.isArray(entry.children) ? entry.children : [];

                    return (
                      <React.Fragment key={`mom-mobile-${entry.id}`}>
                        <tr
                          className="border-b border-slate-100 text-sm cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            setExpandedCategoryId(isExpanded ? null : entry.id);
                          }}
                        >
                          <td className="py-2 pr-2 align-top font-medium text-slate-900 break-words leading-snug">
                            {entry.name}
                          </td>
                          <td className="py-2 pl-2 text-right whitespace-nowrap text-[15px] text-slate-700 tabular-nums">{formatCurrencyRounded(entry.currentValue)}</td>
                          <td className="py-2 pl-2 text-right whitespace-nowrap text-[15px] text-slate-600 tabular-nums">{formatCurrencyRounded(entry.previousValue)}</td>
                          <td className={`py-2 pl-2 text-right whitespace-nowrap text-[15px] font-semibold tabular-nums ${getChangeClass(entry.difference)}`}>
                            {getChangeArrow(entry.difference)} {formatCurrencyRounded(Math.abs(Number(entry.difference || 0)))}
                          </td>
                        </tr>
                        {isExpanded && children.map((child) => (
                          <tr key={`mom-mobile-child-${entry.id}-${child.id}`} className="border-b border-slate-100 text-sm bg-slate-50/80">
                            <td className="py-2 pr-2 pl-4 align-top text-slate-700 break-words leading-snug">
                              <span className="text-slate-400">- </span>{child.name}
                            </td>
                            <td className="py-2 pl-2 text-right whitespace-nowrap text-slate-700 tabular-nums">{formatCurrencyRounded(child.currentValue)}</td>
                            <td className="py-2 pl-2 text-right whitespace-nowrap text-slate-600 tabular-nums">{formatCurrencyRounded(child.previousValue)}</td>
                            <td className={`py-2 pl-2 text-right whitespace-nowrap font-semibold tabular-nums ${getChangeClass(child.difference)}`}>
                              {getChangeArrow(child.difference)} {formatCurrencyRounded(Math.abs(Number(child.difference || 0)))}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 text-sm font-semibold">
                    <td className="py-2 pr-2 text-slate-900">Total</td>
                    <td className="py-2 pl-2 text-right whitespace-nowrap text-slate-800 tabular-nums">{formatCurrencyRounded(totals.currentValue)}</td>
                    <td className="py-2 pl-2 text-right whitespace-nowrap text-slate-700 tabular-nums">{formatCurrencyRounded(totals.previousValue)}</td>
                    <td className={`py-2 pl-2 text-right whitespace-nowrap tabular-nums ${getChangeClass(totals.difference)}`}>
                      {getChangeArrow(totals.difference)} {formatCurrencyRounded(Math.abs(totals.difference))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="hidden sm:block overflow-x-auto overflow-y-visible">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-slate-300 text-left text-xs uppercase tracking-wide text-slate-600">
                    <th className="sticky top-0 z-10 bg-slate-100 py-3 pr-3 font-semibold">Category</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-3 px-3 font-semibold text-right">Current Month</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-3 px-3 font-semibold text-right">Previous Month</th>
                    <th className="sticky top-0 z-10 bg-slate-100 py-3 pl-3 font-semibold text-right">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((entry) => {
                    const isExpanded = expandedCategoryId === entry.id;
                    const children = Array.isArray(entry.children) ? entry.children : [];

                    return (
                      <React.Fragment key={`mom-desktop-${entry.id}`}>
                        <tr
                          className="border-b border-slate-100 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            setExpandedCategoryId(isExpanded ? null : entry.id);
                          }}
                        >
                          <td className="py-2.5 pr-3 font-medium text-slate-900">{entry.name}</td>
                          <td className="py-2.5 px-3 text-right text-slate-800">{formatCurrencyRounded(entry.currentValue)}</td>
                          <td className="py-2.5 px-3 text-right text-slate-700">{formatCurrencyRounded(entry.previousValue)}</td>
                          <td className={`py-2.5 pl-3 text-right font-semibold ${getChangeClass(entry.difference)}`}>
                            {getChangeArrow(entry.difference)} {formatCurrencyRounded(Math.abs(Number(entry.difference || 0)))}
                          </td>
                        </tr>
                        {isExpanded && children.map((child) => (
                          <tr key={`mom-desktop-child-${entry.id}-${child.id}`} className="border-b border-slate-100 bg-slate-50/80">
                            <td className="py-2.5 pr-3 pl-6 text-slate-700">
                              <span className="text-slate-400">- </span>{child.name}
                            </td>
                            <td className="py-2.5 px-3 text-right text-slate-800">{formatCurrencyRounded(child.currentValue)}</td>
                            <td className="py-2.5 px-3 text-right text-slate-700">{formatCurrencyRounded(child.previousValue)}</td>
                            <td className={`py-2.5 pl-3 text-right font-semibold ${getChangeClass(child.difference)}`}>
                              {getChangeArrow(child.difference)} {formatCurrencyRounded(Math.abs(Number(child.difference || 0)))}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 text-sm font-semibold">
                    <td className="py-2.5 pr-3 text-slate-900">Total</td>
                    <td className="py-2.5 px-3 text-right text-slate-800 tabular-nums">{formatCurrencyRounded(totals.currentValue)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-700 tabular-nums">{formatCurrencyRounded(totals.previousValue)}</td>
                    <td className={`py-2.5 pl-3 text-right tabular-nums ${getChangeClass(totals.difference)}`}>
                      {getChangeArrow(totals.difference)} {formatCurrencyRounded(Math.abs(totals.difference))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <p className="text-slate-500">No category comparison data available for this month.</p>
        )}
      </section>
    </div>
  );
}

export default CategoryComparison;
